import React, { useContext, useEffect, useState } from 'react';

import { TaskEntity } from '../../tasks/Task.interface';
import {
  useCreateProductDesignOutput,
  useProductEntityPrefixId,
  useProductViewParams,
  useUpdateProductDesignOutput
} from '../Product.hooks';
import {
  BaseProductBoardColumnProps,
  ProductDesignOutput
} from '../Product.interface';
import { ProductBoardContext } from './ProductBoard.context';
import { ProductBoardColumnEntity } from './ProductBoardColumn';
import ProductBoardItem from './ProductBoardItem';

export type ProductBoardDesignOutputProps = BaseProductBoardColumnProps<
  ProductDesignOutput,
  'designOutput'
>;

const ProductBoardDesignOutput: React.FC<ProductBoardDesignOutputProps> = (
  props: ProductBoardDesignOutputProps
) => {
  const {
    product,
    collapsed,
    designOutput: initialDesignOutput,
    onCancel,
    onError,
    onSave
  } = props;
  const entity = ProductBoardColumnEntity.DesignOutput;
  const params: string = useProductViewParams();
  const { toggleSelected, isSelected } = useContext(ProductBoardContext);
  const [designOutput, setDesignOutput] = useState(initialDesignOutput);
  useEffect(() => {
    if (initialDesignOutput) {
      setDesignOutput(initialDesignOutput);
    }
  }, [initialDesignOutput]);
  const {
    createdProductDesignOutput,
    isCreatingProductDesignOutput,
    createProductDesignOutputError,
    createProductDesignOutput
  } = useCreateProductDesignOutput(product.id);
  const {
    updatedProductDesignOutput,
    isUpdatingProductDesignOutput,
    updateProductDesignOutput
  } = useUpdateProductDesignOutput(product.id, designOutput.id || 0);
  const entityId: string = useProductEntityPrefixId(
    designOutput,
    ProductBoardColumnEntity.DesignOutput
  );

  useEffect(() => {
    if (createdProductDesignOutput) {
      setDesignOutput(createdProductDesignOutput);
      onSave?.(createdProductDesignOutput, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdProductDesignOutput]);

  useEffect(() => {
    if (updatedProductDesignOutput) {
      setDesignOutput(updatedProductDesignOutput);
      onSave?.(updatedProductDesignOutput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedProductDesignOutput]);

  useEffect(() => {
    if (createProductDesignOutputError) {
      onError?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createProductDesignOutputError]);

  return (
    <ProductBoardItem
      product={product}
      taskEntity={designOutput as TaskEntity}
      title={designOutput.title || ''}
      entityId={entityId}
      collapsed={collapsed}
      editable={!designOutput.id}
      isSelected={isSelected(designOutput.id, entity)}
      loading={isUpdatingProductDesignOutput || isCreatingProductDesignOutput}
      viewDetailsLink={`/portal/products/${designOutput.productId}/design-outputs/${designOutput.id}${params}`}
      onSelect={() => toggleSelected(designOutput, entity)}
      onCancel={() => {
        setDesignOutput(initialDesignOutput);
        onCancel?.();
      }}
      onSave={title => {
        designOutput.title = title;
        if (designOutput.id) {
          updateProductDesignOutput(designOutput);
        } else {
          createProductDesignOutput(designOutput);
        }
      }}
    />
  );
};

export default ProductBoardDesignOutput;
